import store from "../../../redux/store";
import {
    FileManagerModeEnum,
    FileUploadStatus,
    LinkUploadStatus,
    listPurchaseType,
    PlanStatus,
    QuotaType,
    ResourceType,
    UploadLinkUrl,
    UserPlanStatus
} from "../../../interfaces/general";
import { advancedResponseValidator, del, get, getAll, post, responseValidator } from "../../../scripts/api";
import API from "../../../urls/api";
import {
    getFileManagerParent,
    getFileManagerParentId,
    getHashPathPublicLinkFolder,
    getWebEnvConfig,
    isFolder,
    toNumberArray,
    searchUrlToObject
} from "../../../scripts/helpers";
import {
    handleClearAllSelectedResources,
    handleSelectResources,
    handleSetContextMenuItems,
    handleSetContextMenuPosition,
    handleSetContextMenuStatus,
    handleSetCutModalStatus,
    handleSetFilteredResources,
    handleSetNextUrl,
    handleSetPagination,
    handleSetQuota,
    handleSetResources,
    handleSetScrollId
} from "./fileManager.actions";
import { createBrowserHistory } from "history";
import PATH from "../../../urls/path";
import { handleSetShareCutModalStatus } from "./sharedFileManager/sharedFileManager.actions";
import { setActivePurchases } from "../../../redux/slices/fileManagerSlice";
import { closeToast, toast } from "mobin-toast";
import {
    setDashboardSynchronize,
    setDuplicatedFilesSynchronize,
    setPurchasePlanSynchronize,
    setReportSynchronize,
    setSessionSynchronize
} from "../../../redux/slices/synchSlice";
import { setUpdateStatusCode } from "../../../redux/slices/duplicatedFilesSlice";
import { v4 as uuidv4 } from "uuid";
import { toastMessages } from "../../../scripts/toastMessages";
import { SearchPageType } from "../../../pages/layout/header/search/search.interface";

export function isResourceSelected(resource: ResourceType) {
    return store
        .getState()
        .fileManager.selectedResources.map((resource: any) => resource.id)
        .includes(resource.id);
}

export function isDefaultSelected(resource: ResourceType) {
    const defaultSelectedItems = store.getState().fileManager.defaultSelectedResourse.item;
    return defaultSelectedItems.includes(resource.obj_hash!) || defaultSelectedItems.includes(resource.id.toString());
}

export function setContextMenu(status: boolean, position: { x: number; y: number }, items: string[]) {
    handleSetContextMenuItems(items);
    handleSetContextMenuStatus(status);
    handleSetContextMenuPosition(position);
}

export function updateQuota() {
    getQuota().then((data: any) => {
        const dispatch = store.dispatch;
        get((_data, status) => {
            if (advancedResponseValidator(_data, status)) {
                const serverTime = _data.datetime_now;
                getAll(
                    (__data: any, status) => {
                        if (advancedResponseValidator(__data, status)) {
                            dispatch(setActivePurchases(planPreprocessing(serverTime, data, __data.result)));
                        }
                    },
                    API.plan.purchases("v4"),
                    { status: PlanStatus.Success, is_active: true }
                );
            }
        }, API.serverTime("v1"));
        handleSetQuota(data as QuotaType);
    });
}

export async function syncSearchFileManager(scrollId?: string, signal?: AbortSignal) {
    const resources = store.getState().fileManager.resources;
    return new Promise((resolve) => {
        const searchUrlObject = searchUrlToObject();
        delete searchUrlObject["pageType"];
        delete searchUrlObject["dateOption"];
        delete searchUrlObject["owners_id"];
        post(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    if (data.results) {
                        if (resources) {
                            handleSetResources([...resources, ...data.results]);
                            handleSetFilteredResources([...resources, ...data.results]);
                        } else {
                            handleSetResources([...data.results]);
                            handleSetFilteredResources([...data.results]);
                        }
                        if (!scrollId) {
                            resolve({ scrollId: data.scroll_id, result: data.results, count: data.count });
                        }
                    } else {
                        if (!scrollId) {
                            handleSetResources([]);
                            handleSetFilteredResources([]);
                        }
                    }
                    resolve({ scrollId: undefined, result: data.results, count: data.count });
                    handleSetScrollId(data.scroll_id);
                }
            },
            API.fileManager.search(),
            scrollId
                ? { scroll_id: scrollId }
                : {
                      ...searchUrlObject,
                      next_page: true,
                      page_size: 40
                  },
            undefined,
            signal
        );
    });
}

export async function syncShareSearchFileManager(nextUrl?: string, signal?: AbortSignal) {
    const resources = store.getState().fileManager.resources;
    return new Promise((resolve) => {
        const searchUrlObject = searchUrlToObject();
        delete searchUrlObject["pageType"];
        delete searchUrlObject["dateOption"];
        delete searchUrlObject["parent_name"];
        delete searchUrlObject["is_favorite"];
        delete searchUrlObject["is_trash"];

        searchUrlObject["owners_id"] = toNumberArray(searchUrlObject["owners_id"]);
        if (!searchUrlObject["owners_id"].length) delete searchUrlObject["owners_id"];
        post(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    const temp = data.results
                        ? data.results.map((item: any) => ({
                              ...item.obj,
                              shareData: { id: item.id, access: item.access, parent: item.parent, created_at: item.created_at }
                          }))
                        : data.results;
                    if (temp) {
                        if (resources) {
                            handleSetResources([...resources, ...temp]);
                            handleSetFilteredResources([...resources, ...temp]);
                        } else {
                            handleSetResources([...temp]);
                            handleSetFilteredResources([...temp]);
                        }
                        if (!nextUrl) {
                            resolve({ nextUrl: data.next, result: temp, count: data.count });
                        }
                    } else {
                        if (!nextUrl) {
                            handleSetResources([]);
                            handleSetFilteredResources([]);
                        }
                    }
                    resolve({ nextUrl: null, result: temp, count: data.count });
                    handleSetNextUrl(data.next);
                }
            },
            nextUrl || API.fileManager.searchShare(),
            Object.keys(searchUrlObject).length ? searchUrlObject : {},
            undefined,
            signal
        );
    });
}

const getAllObject = (params: any, linkObject: any) => {
    const location = createBrowserHistory().location;
    if (![PATH.plan, PATH.settings, PATH.planHistory, PATH.planRecommendation, PATH.duplicatedFiles].includes(location.pathname)) {
        getAll(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    if (!store.getState().fileManager.resources) {
                        handleSetResources([...linkObject, ...data.result]);
                    }
                }
            },
            API.fileManager.list(),
            params
        );
    }
};

const dispatch = store.dispatch;
export async function syncFileManager(quotaStatus: boolean = false, signal?: any) {
    dispatch(setUpdateStatusCode(uuidv4()));

    handleSetResources(undefined);
    const location = createBrowserHistory().location;
    const params = {
        parent: getFileManagerParent(),
        is_trash: false,
        is_favorite: undefined as any,
        recursive: false
    };
    if (location.pathname === PATH.trash) {
        params.is_trash = true;
        params.recursive = true;
    }
    if (location.pathname === PATH.favorite) {
        params.is_favorite = true;
        params.recursive = true;
        params.is_trash = false;
    }
    if (location.pathname === PATH.shared) {
        const linkObject: any[] = [];
        const params = new URLSearchParams(window.location.search);
        if (params.get("path")) {
            const data = await getSharedObject(params.get("path")!);
            if (data !== undefined) {
                await getAll(
                    (data, status) => {
                        if (advancedResponseValidator(data, status)) {
                            const user = store.getState().auth.user;
                            data.result.map((item: UploadLinkUrl) => {
                                if (item.status !== LinkUploadStatus.DONE) {
                                    linkObject.push({
                                        id: item.id,
                                        download_url: item.url,
                                        last_opened: Number(item.updated_at),
                                        last_modified: Number(item.updated_at),
                                        created_at: Number(item.created_at),
                                        user: user!,
                                        name: item.name ? item.name : item.url,
                                        key: "uploadLink",
                                        size: item.size,
                                        has_cover: false,
                                        description: null,
                                        version: 0,
                                        type: "text/plain",
                                        level: 0,
                                        obj_hash: "",
                                        shareData: {
                                            id: item.id,
                                            access: 0,
                                            parent: item.parent,
                                            created_at: item.created_at
                                        },
                                        linkUploadStatus: item.status,
                                        from_link: true
                                    });
                                }
                            });
                        }
                    },
                    API.storage.list(),
                    {
                        parent: data.id,
                        is_trash: false,
                        is_favorite: undefined as any,
                        recursive: false
                    }
                );
            }
        }
        getAll(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    const temp = data.result.map((item: any) => ({
                        ...item.obj,
                        shareData: { id: item.id, access: item.access, parent: item.parent, created_at: item.created_at }
                    }));
                    if (!store.getState().fileManager.resources) {
                        handleSetResources([...linkObject, ...temp]);
                    }
                } else if (status === 404) {
                    createBrowserHistory().push("404");
                }
            },
            API.share.list(),
            {
                obj_hash: getFileManagerParent(),
                recursive: false
            }
        );
    } else if (location.pathname === PATH.search) {
        const pageType = location.search.includes("q") ? searchUrlToObject()?.pageType : undefined;
        if (pageType === SearchPageType.WITH_SHARE_ME) await syncShareSearchFileManager();
        else await syncSearchFileManager();
    } else if (location.pathname === PATH.publicFolder) {
        getAll(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    const temp = data.result.map((item: any) => ({
                        ...item.obj,
                        shareData: { id: item.id, access: item.access, parent: item.parent, created_at: item.created_at }
                    }));
                    if (!store.getState().fileManager.resources) {
                        handleSetResources(temp);
                    }
                } else {
                    createBrowserHistory().push("404");
                }
            },
            API.share.list(),
            {
                obj_hash: getHashPathPublicLinkFolder(),
                recursive: false
            }
        );
    } else if (location.pathname === PATH.dashboard) {
        store.dispatch(setDashboardSynchronize(!store.getState().synchronization.dashboardSynchronize));
        get(
            (data, status) => {
                if (responseValidator(status)) {
                    handleSetFilteredResources(data);
                    handleSetResources(data);
                }
            },
            API.fileManager.suggest(),
            { recent: 4, weekly: 2, monthly: 2 },
            "fa",
            true,
            signal
        );
    } else {
        const linkObject: ResourceType[] = [];
        if (location.pathname === PATH.files) {
            await getAll(
                (data, status) => {
                    if (advancedResponseValidator(data, status)) {
                        const user = store.getState().auth.user;
                        data.result.map((item: UploadLinkUrl) => {
                            if (item.status !== LinkUploadStatus.DONE) {
                                linkObject.push({
                                    id: item.id,
                                    name: item.name ? item.name : item.url,
                                    download_url: item.url,
                                    folder_metadata: null,
                                    key: "uploadLink",
                                    size: item.size,
                                    is_complete: true,
                                    level: 0,
                                    user: user!,
                                    has_cover: false,
                                    last_modified: Number(item.updated_at),
                                    created_at: Number(item.created_at),
                                    is_favorite: false,
                                    has_link: false,
                                    is_shared: false,
                                    from_link: true,
                                    parent: item.parent,
                                    linkUploadStatus: item.status
                                });
                            }
                        });
                        getAllObject(params, linkObject);
                    }
                },
                API.storage.list(),
                {
                    parent: getFileManagerParent() ? getFileManagerParent() : 0,
                    is_trash: false,
                    is_favorite: undefined as any,
                    recursive: false
                },
                "fa",
                signal
            );
        } else getAllObject(params, linkObject);

        const searchParams = new URLSearchParams(window.location.search);
        if (location.pathname === PATH.report) store.dispatch(setReportSynchronize(!store.getState().synchronization.reportSynchronize));
        else if (location.pathname === PATH.duplicatedFiles)
            store.dispatch(setDuplicatedFilesSynchronize(!store.getState().synchronization.duplicatedFilesSynchronize));
        else if (location.pathname === PATH.settings && searchParams.get("tab") === "sessions")
            store.dispatch(setSessionSynchronize(!store.getState().synchronization.sessionSynchronize));
        else if (location.pathname === PATH.plan)
            store.dispatch(setPurchasePlanSynchronize(!store.getState().synchronization.purchasePlanSynchronize));
    }
    if (quotaStatus && store.getState().auth.authStatus !== "INVALID") {
        updateQuota();
    }
}

export function getObjects(id: any | undefined, shared: boolean = true) {
    const location = createBrowserHistory().location;
    const url = location.pathname === PATH.shared && shared ? API.share.list() : API.fileManager.list();
    return new Promise<ResourceType[]>((resolve) => {
        getAll(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    location.pathname === PATH.shared && shared
                        ? resolve(
                              data.result.map((item: any) => ({
                                  ...item.obj,
                                  shareData: { id: item.id, access: item.access, parent: item.parent, created_at: item.created_at }
                              }))
                          )
                        : resolve(data.result);
                } else if (status === 404) {
                    createBrowserHistory().push("404");
                }
            },
            url,
            location.pathname === PATH.shared && shared
                ? { obj_hash: id ? id : undefined, is_trash: false }
                : {
                      parent: id ? id : undefined,
                      is_trash: false
                  }
        );
    });
}

export function getObject(id: number) {
    return new Promise<ResourceType>((resolve) => {
        get((data, status) => {
            if (advancedResponseValidator(data, status)) {
                resolve(data);
            }
        }, API.fileManager.getObject(id));
    });
}

export function getSharedObject(hash: string) {
    return new Promise<ResourceType>((resolve) => {
        get(
            (data, status) => {
                if (advancedResponseValidator(data, status)) {
                    resolve(data.obj);
                }
            },
            API.share.getObject("v4", hash)
        );
    });
}

export function getQuota() {
    return new Promise((resolve) => {
        get((data, status) => {
            if (advancedResponseValidator(data, status)) {
                resolve(data);
            }
        }, API.fileManager.quota());
    });
}

const planPreprocessing = (serverTime: number, data: QuotaType, purchasesPlans: listPurchaseType[]) => {
    // normal state
    const plans: any = [...purchasesPlans];

    if (+getWebEnvConfig("DEFAULT_TOTAL_QUOTA") !== 0)
        plans.unshift({
            created_at: 0,
            expires_at: undefined,
            id: 0,
            is_active: true,
            rrn: "",
            status: 1,
            plan: {
                duration: 0,
                id: 0,
                price: 0,
                volume: +getWebEnvConfig("DEFAULT_TOTAL_QUOTA"),
                label: "فضای ذخیره‌سازی ابرهمراهی رایگان شما"
            }
        });
    const userPlanStatus: UserPlanStatus = data.total_plan + data.total_gift < data.used ? UserPlanStatus.DANGER : UserPlanStatus.NORMAL;
    return { userPlanStatus: userPlanStatus, serverTime: serverTime, purchasesPlans: plans };
};

export function checkIsFileExists(resources: ResourceType[], file: any) {
    return resources.map((resource) => resource.name).includes(file.name);
}

function CopyResource(resource: ResourceType, copyInRoot: boolean, parent?: number) {
    if (resource) {
        return new Promise(async (resolve) => {
            post(
                (data, status) => {
                    if (responseValidator(status)) {
                        resolve({ quota: false, status: true });
                    } else {
                        if (status === 413) {
                            resolve({ quota: true, status: false });
                        } else {
                            resolve({ quota: false, status: false });
                        }
                    }
                },
                API.fileManager.copy(),
                {
                    source_obj_id: resource.id,
                    target_parent_id: parent ? parent : copyInRoot ? null : resource.parent,
                    new_name: resource.name
                }
            );
        });
    }
}

export function copyResources(mode: FileManagerModeEnum, copyInRoot: boolean = false, isInShareFileManager: boolean = false) {
    const fileManager = store.getState().fileManager;
    const location = createBrowserHistory().location;
    handleSetContextMenuStatus(false);
    return new Promise(async (resolve) => {
        let successCopyCount = 0;
        let quota = false;
        let sharedObject = undefined;
        if (location.pathname === PATH.shared && !copyInRoot) {
            sharedObject = await getSharedObject(getFileManagerParent());
        }
        for (let i = 0; i < fileManager.selectedResources.length; i++) {
            const copy: any = await CopyResource(fileManager.selectedResources[i], copyInRoot, sharedObject?.id);
            if (!quota) quota = copy.quota;
            if (copy.status) successCopyCount++;
            if (i === fileManager.selectedResources.length - 1) {
                resolve({
                    count: fileManager.selectedResources.length,
                    successCount: successCopyCount,
                    quota,
                    isInShareFileManager
                });
                if (!fileManager.previewModalStatus) handleClearAllSelectedResources();
                if (!copyInRoot) {
                    syncFileManager(true);
                }
            }
        }
    });
}

function cutResource(id: number, targetId: number | null) {
    return new Promise((resolve) => {
        post(
            (data, status) => {
                if (responseValidator(status)) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            },
            API.fileManager.move(),
            { source_obj_id: id, target_parent_id: targetId ? targetId : null }
        );
    });
}

export function cutResources(id: number | undefined, targetId: number | null, resourcesToMove?: ResourceType[], undo?: boolean) {
    const fileManager = store.getState().fileManager;
    const resources = resourcesToMove ? resourcesToMove : fileManager.selectedResources;
    const target = resourcesToMove || id ? getFileManagerParentId() : targetId;
    return new Promise(async (resolve) => {
        let successCount = 0;
        if (!undo) toast({ title: "در حال انتقال ...", type: "promise", id: "cut" });
        if (id) {
            cutResource(id, targetId).then((status) => {
                if (status) {
                    if (!undo) {
                        toast({
                            title: toastMessages(78),
                            type: "success",
                            callbackFunction: () => {
                                return new Promise((resolve2) => {
                                    cutResources(id, target, undefined, true).then(() => resolve2(true));
                                });
                            }
                        });
                    }
                }
                if (!fileManager.previewModalStatus) handleClearAllSelectedResources();
                syncFileManager(true);
            });
        } else {
            for (let i = 0; i < resources.length; i++) {
                if (await cutResource(resources[i].id, target)) {
                    successCount++;
                }
                if (i === resources.length - 1) {
                    resolve(true);
                    if (!fileManager.previewModalStatus) handleClearAllSelectedResources();
                    handleSetShareCutModalStatus(false);
                    handleSetCutModalStatus(false);
                    syncFileManager(true);
                    if (successCount > 0) {
                        if (!undo) {
                            toast({
                                title: toastMessages(77, [resources.length]),
                                type: "success",
                                callbackFunction: () => {
                                    return new Promise((resolve2) => {
                                        cutResources(id, target, resources, true).then(() => resolve2(2));
                                    });
                                }
                            });
                        }
                    } else {
                        toast({ title: toastMessages(76), type: "error" });
                    }
                }
            }
        }
        if (!undo) closeToast("cut");
    });
}

export function downloadZip(ids: number[], key: string | undefined = undefined) {
    return new Promise((resolve) => {
        post(
            (data, status) => {
                resolve({ key: data.key, link: data.download_link });
            },
            API.fileManager.zipObjects(),
            { obj_hashes: ids, key }
        );
    });
}

export function downloadResource() {
    const fileManager = store.getState().fileManager;
    if (fileManager.selectedResources.length > 0) {
        const a = document.createElement("a");
        document.body.appendChild(a);
        if (fileManager.selectedResources.length === 1 && !fileManager.selectedResources[0].folder_metadata && fileManager.selectedResources[0].key) {
            const file = fileManager.selectedResources[0];
            a.href = file.download_url;
            a.click();
            if (!fileManager.previewModalStatus) handleClearAllSelectedResources();
        } else if (
            fileManager.selectedResources.length > 1 ||
            fileManager.selectedResources[0].folder_metadata ||
            !fileManager.selectedResources[0].key
        ) {
            const chunks = sliceIntoChunks(fileManager.selectedResources, Number(getWebEnvConfig("zipObjectsChunkSize")));
            let key: string | undefined = undefined;
            let link = "";
            return new Promise(async (resolve) => {
                for (let i = 0; i < chunks.length; i++) {
                    const data = (await downloadZip(
                        chunks[i].map((item: ResourceType) => (item as any).obj_hash),
                        key
                    )) as any;
                    if (i === 0) {
                        key = data.key;
                        link = data.link;
                    }
                    if (i === chunks.length - 1) {
                        a.href = link;
                        a.setAttribute("target", "_blank");
                        a.click();
                    }
                }
                if (!fileManager.previewModalStatus) handleClearAllSelectedResources();
            });
        }
    }
}

function sliceIntoChunks(arr: any, chunkSize: number) {
    const res = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
        const chunk = arr.slice(i, i + chunkSize);
        res.push(chunk);
    }
    return res;
}

export function trashResources(resources?: ResourceType[]) {
    // const inProgressUploadsParentIds = store
    //     .getState()
    //     .upload.filesToUpload.filter((item) => item.status !== FileUploadStatus.DONE)
    //     .map((item) => item.parentId);
    const fileManager = store.getState().fileManager;
    const chunks = sliceIntoChunks(resources ? resources : fileManager.selectedResources, 100);
    return new Promise((resolve) => {
        chunks.forEach((chunk, index) => {
            del(
                (data, status) => {
                    if (!advancedResponseValidator(data, status)) {
                        return;
                    }
                    if (index === chunks.length - 1) {
                        handleClearAllSelectedResources();
                        if (fileManager.resources)
                            handleSetResources(
                                fileManager.resources.filter(
                                    (resource) => !fileManager.selectedResources.map((item) => item.id).includes(resource.id)
                                )
                            );
                        if (!resources) {
                            toast({
                                title: toastMessages(75),
                                type: "success",
                                callbackFunction: () => {
                                    const items = chunks.flat(1);
                                    return new Promise(async (resolve) => {
                                        for (let i = 0; i < items.length; i++) {
                                            await restoreResource(items[i].id);
                                            if (i === items.length - 1) {
                                                syncFileManager(true);
                                                resolve(true);
                                            }
                                        }
                                    });
                                }
                            });
                        }
                    }
                    resolve(true);
                },
                API.fileManager.trash(),
                { obj_ids: chunk.map((resource: ResourceType) => resource.id) }
            );
        });
    });
}

export function deleteResources() {
    const fileManager = store.getState().fileManager;
    const chunks = sliceIntoChunks(fileManager.selectedResources, 100);
    return new Promise((resolve) => {
        chunks.forEach((chunk, index) => {
            del(
                (data, status) => {
                    if (!advancedResponseValidator(data, status)) {
                        return;
                    }
                    if (index === chunks.length - 1) {
                        handleClearAllSelectedResources();
                        if (fileManager.resources)
                            handleSetResources(
                                fileManager.resources.filter(
                                    (resource) => !fileManager.selectedResources.map((item) => item.id).includes(resource.id)
                                )
                            );
                        updateQuota();
                        toast({ title: toastMessages(74), type: "success" });
                    }
                    resolve(true);
                },
                API.fileManager.delete(),
                { version_groups: chunk.map((resource: ResourceType) => resource.version_group) }
            );
        });
    });
}

export function addToFavoriteResources(mode: FileManagerModeEnum, resources?: ResourceType[]) {
    const fileManager = store.getState().fileManager;
    handleSetContextMenuStatus(false);
    const chunks = sliceIntoChunks(resources ? resources : fileManager.selectedResources, 1000);
    handleSetContextMenuStatus(false);
    return new Promise((resolve) => {
        chunks.forEach((chunk, index) => {
            post(
                (data, status) => {
                    if (!advancedResponseValidator(data, status)) {
                        return;
                    }
                    if (index === chunks.length - 1) {
                        syncFileManager(false);
                        toast({
                            title: toastMessages(73),
                            type: "success",
                            callbackFunction: () => {
                                const items = chunks.flat(1);
                                return new Promise(async (resolve) => {
                                    deleteFromFavoriteResources(mode, items).then(() => resolve(true));
                                });
                            }
                        });
                        if (fileManager.previewModalStatus) {
                            handleSelectResources([{ ...chunks[0][0], is_favorite: true }]);
                        } else handleClearAllSelectedResources();
                    }
                    resolve(true);
                },
                API.fileManager.addFavorite(),
                {
                    obj_ids: resources
                        ? chunk.map((resource: ResourceType) => resource.id)
                        : chunk.filter((resource: any) => !resource.is_favorite).map((resource: ResourceType) => resource.id)
                }
            );
        });
    });
}

export function deleteFromFavoriteResources(mode: FileManagerModeEnum, resources?: ResourceType[]) {
    const fileManager = store.getState().fileManager;
    handleSetContextMenuStatus(false);
    const chunks = sliceIntoChunks(resources ? resources : fileManager.selectedResources, 1000);
    return new Promise((resolve) => {
        chunks.forEach((chunk, index) => {
            del(
                (data, status) => {
                    if (!advancedResponseValidator(data, status)) {
                        return;
                    }
                    if (index === chunks.length - 1) {
                        if (fileManager.previewModalStatus) {
                            handleSelectResources([{ ...chunks[0][0], is_favorite: false }]);
                        } else {
                            handleClearAllSelectedResources();
                        }
                        syncFileManager(false);
                        if (!resources) {
                            toast({
                                title: toastMessages(72, [fileManager.selectedResources.length]),
                                type: "success",
                                callbackFunction: () => {
                                    const items = chunks.flat(1);
                                    return new Promise(async (resolve) => {
                                        addToFavoriteResources(mode, items).then(() => resolve(true));
                                    });
                                }
                            });
                        }
                    }
                    resolve(true);
                },
                API.fileManager.deleteFavorite(),
                { obj_ids: chunk.map((resource: ResourceType) => resource.id) }
            );
        });
    });
}

function restoreResource(id: number) {
    return new Promise((resolve) => {
        post(
            (data, status) => {
                if (responseValidator(status)) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            },
            API.fileManager.restore(),
            { obj_id: id }
        );
    });
}

export function restoreResources(mode: FileManagerModeEnum) {
    const fileManager = store.getState().fileManager;
    return new Promise(async (resolve) => {
        let successCount = 0;
        const resources: ResourceType[] = [];
        for (let i = 0; i < fileManager.selectedResources.length; i++) {
            if (await restoreResource(fileManager.selectedResources[i].id)) {
                successCount++;
                resources.push(fileManager.selectedResources[i]);
            }
            if (i === fileManager.selectedResources.length - 1) {
                resolve({ count: fileManager.selectedResources.length, successCount: successCount, doneResources: resources });
                handleClearAllSelectedResources();
                syncFileManager(false);
                handleSetContextMenuStatus(false);
            }
        }
    });
}

function unsharedResource(id: number) {
    return new Promise((resolve) => {
        del(
            (data, status) => {
                if (responseValidator(status)) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            },
            API.share.unshare("v2", id),
            { object_id: id }
        );
    });
}

export function unsharedResources(trash: boolean) {
    const fileManager = store.getState().fileManager;
    return new Promise(async (resolve) => {
        if (!trash) {
            let successCount = 0;
            for (let i = 0; i < fileManager.selectedResources.length; i++) {
                if (await unsharedResource(fileManager.selectedResources[i].id)) {
                    successCount++;
                }
                if (i === fileManager.selectedResources.length - 1) {
                    resolve({ count: fileManager.selectedResources.length, successCount: successCount });
                    handleClearAllSelectedResources();
                    syncFileManager();
                    handleSetContextMenuStatus(false);
                }
            }
        } else {
            trashResources().finally(() => {
                resolve({ trash: true });
            });
        }
    });
}

export const sortFileManager = () => {
    const location = createBrowserHistory().location;
    const fileManager = store.getState().fileManager;
    let sortedResources: ResourceType[] = [];
    let unsuccessfulUploadLinkFiles: any[] = [];
    if (fileManager.resources)
        fileManager.resources.map((item: any) => {
            if (!!item.linkUploadStatus && item.linkUploadStatus !== LinkUploadStatus.DONE && item.from_link) {
                unsuccessfulUploadLinkFiles.push(item);
            }
        });
    const resources: ResourceType[] = fileManager.resources
        ? fileManager.resources.filter((item: any) => !unsuccessfulUploadLinkFiles.map((item) => item.id).includes(item.id))
        : [];
    if (location.pathname !== PATH.dashboard) {
        const sortOption = fileManager.sort;
        if (sortOption.value) {
            if (sortOption.value === "name") {
                sortedResources = [...resources].sort((a, b) => {
                    if (a.name > b.name) {
                        return sortOption.status === "+" ? 1 : -1;
                    } else {
                        return sortOption.status === "-" ? 1 : -1;
                    }
                });
            }
            if (sortOption.value === "size") {
                sortedResources = [...resources].sort((a, b) => {
                    if (sortOption.status === "+") {
                        return a.size - b.size;
                    } else {
                        return b.size - a.size;
                    }
                });
            }
            if (sortOption.value === "updated_at") {
                sortedResources = [...resources].sort((a, b) => {
                    if (a.last_modified > b.last_modified) {
                        return sortOption.status === "+" ? 1 : -1;
                    } else {
                        return sortOption.status === "-" ? 1 : -1;
                    }
                });
            }
        }
        sortedResources = sortedResources.sort((a, b) => {
            if (isFolder(a)) {
                return a.size - b.size;
            } else {
                return 1;
            }
        });
        return [...unsuccessfulUploadLinkFiles, ...sortedResources];
    } else {
        return resources;
    }
};

export const findSelectedPage = (sortedList: ResourceType[]) => {
    const fileManager = store.getState().fileManager;
    if (fileManager.defaultSelectedResourse.item) {
        const id = fileManager.defaultSelectedResourse.item[0];
        const checkIsIdOrHash = /^-?\d+$/.test(id!);

        let itemIndex: any = {};
        sortedList.find((el, index) => {
            if (checkIsIdOrHash ? el.id === Number(id) : el.obj_hash === id) {
                return (itemIndex = {
                    item: el,
                    index: index
                });
            }
        });

        if (itemIndex.item) {
            let page = Math.ceil(itemIndex.index / fileManager.pagination.pageSize);
            if (page === 0) page += 1;
            else if (itemIndex.index % fileManager.pagination.pageSize === 0) page += 1;
            if (fileManager.pagination.page !== page) handleSetPagination({ page: page, pageSize: 50 });
        }
    }
};

export const paginationMouseDown = (e: any) => {
    if (document.getElementById("paginationContainer")!.contains(e.target) && !document.getElementById("pagination")!.contains(e.target)) {
        handleClearAllSelectedResources();
    }
};
